import {ChakraStylesConfig} from 'chakra-react-select';
import {omit} from 'lodash';
import moment from 'moment';
import React, {memo, useState} from 'react';
import {HiExclamationCircle, HiOutlineIdentification} from 'react-icons/hi';
import {useDispatch, useSelector} from 'react-redux';
import SchoolsDropdown from '~/components/dropdowns/SchoolsDropdown';
import {
  AppDatePicker,
  Row,
  UiBox,
  UiButton,
  UiCenter,
  UiCheckbox,
  UiFormControl,
  UiFormErrorMessage,
  UiIcon,
  UiImage,
  UiInput,
  UiRadio,
  UiSimpleGrid,
  UiText,
  UploadResponse,
} from '~/components/elements';
import {Col} from '~/components/elements/grid/Col';
import {UiApplicationsFormsLayout} from '~/components/elements/UiApplicationsFormsLayout';
import {UiCard} from '~/components/elements/UiCard';
import TextModal from '~/components/modals/TextModal';
import UploadImageModal from '~/components/modals/UploadImageModal';
import DatePickerCustomInput from '~/components/shared/DatePickerCustomInput';
import StudentFormLabel from '~/components/shared/StudentFormLabel';
import {STUDENT_ENDPOINTS, TODAY_DEEPLINK} from '~/constants';
import {useBoolBag, useInput, useNotification, useRequestState} from '~/hooks';
import {AppDispatch} from '~/redux/root-store';
import {getSchoolsDropdown} from '~/redux/schools/slice';
import {SchoolEntity} from '~/redux/schools/types';
import {addStudentAmbassadorRegister} from '~/redux/students/thunk';
import {StudentUser} from '~/redux/students/types';
import {formatDate, YEAR_FORMAT} from '~/utils/helpers';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';
import {RootState} from '~/redux/root-reducer';

const customChakraStyles: ChakraStylesConfig = {
  singleValue: (provided: any) => {
    const fontWeight = '400';
    return {...provided, fontWeight};
  },
  placeholder: (provided: any) => {
    return {...provided, fontWeight: '400'};
  },
  dropdownIndicator: (provided: any) => {
    return {...provided, background: 'surfaceLight'};
  },
};

interface Props {}

const AmbassadorForm = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const [isShowUploadModal, setIsShowUploadModal] = useState(false);
  const [isShowSuccessModal, setIsShowSuccessModal] = useState(false);
  const {data: schools, paginator, loading: getSchoolsLoading} = useSelector(getSchoolsDropdown);
  const isWebview = useSelector((state: RootState) => state.questionnaires.isWebview);
  const [isClosed, setIsClosed] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [schoolNameInput, setSchoolNameInput] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<UploadResponse[]>([]);
  const {addMessage, addValidationMessage} = useNotification(dispatch);
  const {loading} = useRequestState(addStudentAmbassadorRegister);

  const {boolBag, toggleBoolBag} = useBoolBag({agree: false, hasEmailAlready: false});

  const rules = {
    firstName: {
      presence: {allowEmpty: false},
    },
    lastName: {
      presence: {allowEmpty: false},
    },
    schoolId: {
      presence: {allowEmpty: false},
    },
    graduate: {
      presence: {allowEmpty: false},
    },
    studentId: {
      presence: {allowEmpty: false},
    },
    instagramProfileURL: {
      presence: {allowEmpty: false},
      format: {
        pattern: /(?:(?:http|https):\/\/)?(?:www.)?(?:instagram.com|instagr.am|instagr.com)\/(\w+)\/?(.+)/gim,
        message: '^Must be a valid instagram profile URL',
      },
    },
    email: {
      presence: {allowEmpty: false},
      email: {
        message: '^Invalid email address',
      },
    },
    existingEmail: function () {
      if (boolBag.hasEmailAlready) {
        return {
          presence: {allowEmpty: false},
          email: {
            message: '^Invalid email address',
          },
          equality: {
            attribute: 'email',
            message: '^Opps, please deselect this field if your existing account is the same with your student email',
            comparator: function (email: string, existingEmail: string) {
              if (!boolBag.hasEmailAlready) {
                return true;
              }
              return email !== existingEmail;
            },
          },
        };
      }
      return undefined;
    },
  };

  const {inputs, setInputs, validation, trimSpaces} = useInput<StudentUser>(
    {
      firstName: '',
      lastName: '',
      existingEmail: '',
      schoolId: undefined,
      graduate: '',
      studentId: [],
      instagramProfileURL: '',
      email: '',
    },
    {rules},
  );

  const isAllInputsValid = boolBag.hasEmailAlready
    ? Object.values(inputs).every(i => !!i)
    : Object.values(omit(inputs, 'existingEmail')).every(i => !!i);
  const isSchoolValid = schools?.find((school: SchoolEntity) => school?.id === inputs.schoolId);
  const canSubmit = isSchoolValid && boolBag.agree && inputs.studentId?.length && isAllInputsValid;

  const onSubmit = async () => {
    setErrorText('');
    if (validation.getErrors()) {
      return;
    }

    const resAction = await dispatch(addStudentAmbassadorRegister({data: {user: inputs}}));

    if (requestOK(resAction, addStudentAmbassadorRegister)) {
      setIsShowSuccessModal(true);
    } else {
      // @ts-ignore
      if (resAction?.payload?.errMsg) setErrorText(resAction?.payload?.errMsg);
    }
  };

  const onUploadSuccess = (files: UploadResponse[]) => {
    const twoImages = files?.slice(0, 2).map(file => file.accessUrl);
    setUploadedFile(files?.slice(0, 2));
    setInputs({studentId: twoImages});
  };

  const handleOpenImageToNewTab = (url: string) => {
    window.open(url, '_blank');
  };

  const handleChangeEmailValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = trimSpaces(event?.target?.value);
    setInputs({email: value});
  };

  const handleChangeExistingEmailValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = trimSpaces(event?.target?.value);
    setInputs({existingEmail: value});
  };

  const toggleEaseEmailCheckBox = () => {
    toggleBoolBag('hasEmailAlready');
    if (boolBag.hasEmailAlready) {
      setInputs({existingEmail: ''});
    }
  };

  const renderContent = () => {
    if (isClosed)
      return (
        <UiCard variant="rounded" width="90%" bg={{base: 'white', lg: 'darkCream'}} mt={10} py={10}>
          <UiCenter flexDirection="column" fontSize="md">
            <UiText>Applications to join the 2022 Students@Ease Campus Outreach Program are closed.</UiText>
            <UiText mt={5}>If you have questions or concerns, please email us at students@ease-healthcare.com</UiText>
          </UiCenter>
        </UiCard>
      );
    return (
      <UiCard variant="rounded" shadow="none" width="100%" py={2}>
        <UiSimpleGrid columns={2} spacing={{base: 4, lg: 10}} w="100%" mt={5}>
          <UiFormControl id="firstName" isRequired isInvalid={!!validation?.errors?.firstName}>
            <StudentFormLabel>First Name</StudentFormLabel>
            <UiInput
              variant="filled"
              value={inputs.firstName}
              onChange={({target}) => setInputs({firstName: target.value})}
              fontWeight="400"
              height="42px"
              bg="surfaceLight"
              borderRadius={4}
              placeholder="as per student ID"
            />
            {validation?.errors?.firstName && (
              <UiFormErrorMessage>{validation?.errors?.firstName[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
          <UiFormControl id="lastName" isRequired isInvalid={!!validation?.errors?.lastName}>
            <StudentFormLabel>Last Name</StudentFormLabel>
            <UiInput
              variant="filled"
              value={inputs.lastName}
              onChange={({target}) => setInputs({lastName: target.value})}
              fontWeight="400"
              height="42px"
              bg="surfaceLight"
              borderRadius={4}
              placeholder="as per student ID"
            />
            {validation?.errors?.lastName && <UiFormErrorMessage>{validation?.errors?.lastName[0]}</UiFormErrorMessage>}
          </UiFormControl>
        </UiSimpleGrid>
        <UiSimpleGrid columns={1} mt={2} w="100%" spacing={4} alignItems="start">
          <UiBox display="flex" alignItems="center">
            <UiCheckbox size="md" isChecked={boolBag.hasEmailAlready} onChange={toggleEaseEmailCheckBox} mr={4} />
            <StudentFormLabel
              fontStyle="italic"
              fontSize="14px"
              _hover={{cursor: 'pointer'}}
              onClick={toggleEaseEmailCheckBox}
              m={0}
            >
              I have an existing Ease account
            </StudentFormLabel>
          </UiBox>
          {boolBag.hasEmailAlready && (
            <UiFormControl id="existingEmail" isRequired isInvalid={!!validation?.errors?.existingEmail}>
              <UiInput
                variant="filled"
                value={inputs.existingEmail}
                onChange={event => handleChangeExistingEmailValue(event)}
                fontWeight="400"
                height="42px"
                bg="surfaceLight"
                borderRadius={4}
                placeholder="Enter the email of your Ease account"
              />
              {validation?.errors?.existingEmail && (
                <UiFormErrorMessage>{validation?.errors?.existingEmail[0]}</UiFormErrorMessage>
              )}
            </UiFormControl>
          )}
        </UiSimpleGrid>
        <UiSimpleGrid columns={{base: 1, lg: 2}} spacingX={10} spacingY={5} w="100%" mt={5}>
          <UiFormControl id="schoolId" isRequired isInvalid={!!validation?.errors?.schoolId}>
            <StudentFormLabel>What school are you from?</StudentFormLabel>
            <SchoolsDropdown
              onChange={value => {
                setInputs({schoolId: Number(value?.value)});
                setSchoolName('');
              }}
              selectedFontWeight="400"
              isClearable
              noOptionsMessage={() => null}
              onInputChange={(inputValue: string) => {
                setSchoolNameInput(inputValue);
              }}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') {
                  setSchoolName(schoolNameInput);
                }
              }}
              styleControl={{height: '42px', background: 'surfaceLight', borderRadius: 4}}
              customChakraStyles={customChakraStyles}
              placeholder="School name"
            />
            {validation?.errors?.schoolId && <UiFormErrorMessage>{validation?.errors?.schoolId[0]}</UiFormErrorMessage>}
          </UiFormControl>

          {(isSchoolValid || !schoolName) && (
            <UiFormControl id="graduate" isRequired isInvalid={!!validation?.errors?.graduate}>
              <StudentFormLabel>Which year will you graduate?</StudentFormLabel>
              <AppDatePicker
                selected={
                  inputs.graduate && moment(inputs?.graduate).isValid() === true
                    ? new Date(inputs?.graduate)
                    : undefined
                }
                onChange={value => {
                  setInputs({graduate: formatDate(moment(value).endOf('year'))});
                }}
                dropdownMode="select"
                showYearPicker
                dateFormat={YEAR_FORMAT}
                customInput={
                  <DatePickerCustomInput fontWeight="400" height="42px" bg="surfaceLight" borderRadius={4} />
                }
                minDate={moment().subtract(1, 'years').toDate()}
                autoComplete="off"
                placeholderText="YYYY"
              />
              {validation?.errors?.graduate && (
                <UiFormErrorMessage>{validation?.errors?.graduate[0]}</UiFormErrorMessage>
              )}
            </UiFormControl>
          )}
        </UiSimpleGrid>

        {!isSchoolValid && schoolName && (
          <UiText mt={5}>
            Unfortunately, your school is not currently participating of the program. At this time you would not be
            eligible for the Students@Ease Program. However, you may send in a request to students@ease-healthcare.com
            to add your school to the list and we will be in touch with you on next steps
          </UiText>
        )}

        {(isSchoolValid || !schoolName) && (
          <>
            <UiFormControl id="studentId" isRequired isInvalid={!!validation?.errors?.studentId} mt={5}>
              <StudentFormLabel>
                Upload a photo of your student ID - Front and Back (with the date of validity)
              </StudentFormLabel>
              <UiBox borderWidth={1} p={1} borderRadius={spacing.mini} borderColor="lightGrey" flexWrap="nowrap">
                <Row alignItems="center">
                  <UiButton
                    title="Choose file"
                    fontSize="xs"
                    bg="greenLight"
                    minH={spacing.smedium}
                    onClick={() => setIsShowUploadModal(true)}
                  />
                  {!inputs.studentId?.length && (
                    <UiText ml={4} fontSize="xs" color="blackSecondary">
                      No file chosen
                    </UiText>
                  )}
                </Row>
                {uploadedFile?.length > 0 && (
                  <Col my={3}>
                    {uploadedFile?.map(file => (
                      <UiText
                        key={file.accessUrl}
                        _hover={{cursor: 'pointer'}}
                        onClick={() => handleOpenImageToNewTab(file.accessUrl)}
                        ml={{base: 0, lg: 2}}
                      >
                        {file.fileName}
                      </UiText>
                    ))}
                  </Col>
                )}
              </UiBox>
              {validation?.errors?.studentId && (
                <UiFormErrorMessage>{validation?.errors?.studentId[0]}</UiFormErrorMessage>
              )}
            </UiFormControl>

            <UiFormControl id="email" isRequired isInvalid={!!validation?.errors?.email} mt={5}>
              <StudentFormLabel>School Email Address</StudentFormLabel>
              <UiInput
                variant="filled"
                value={inputs.email}
                onChange={event => handleChangeEmailValue(event)}
                fontWeight="400"
                height="42px"
                bg="surfaceLight"
                borderRadius={4}
                placeholder="Must be a valid email address"
              />
              {validation?.errors?.email && <UiFormErrorMessage>{validation?.errors?.email[0]}</UiFormErrorMessage>}
            </UiFormControl>

            <UiFormControl
              id="instagramProfileURL"
              isRequired
              isInvalid={!!validation?.errors?.instagramProfileURL}
              mt={5}
            >
              <StudentFormLabel>Your Instagram Profile URL</StudentFormLabel>
              <UiInput
                variant="filled"
                value={inputs.instagramProfileURL}
                onChange={({target}) => setInputs({instagramProfileURL: target.value})}
                fontWeight="400"
                height="42px"
                bg="surfaceLight"
                borderRadius={4}
                placeholder="https://www.instagram.com/username"
              />
              {validation?.errors?.instagramProfileURL && (
                <UiFormErrorMessage>{validation?.errors?.instagramProfileURL[0]}</UiFormErrorMessage>
              )}
            </UiFormControl>

            <UiFormControl id="agree" isRequired mt={3}>
              <Row alignItems="center" justifyContent="center">
                <UiRadio
                  onClick={() => toggleBoolBag('agree')}
                  isChecked={boolBag.agree}
                  borderColor="dark"
                  size="md"
                />
                <StudentFormLabel
                  _hover={{cursor: 'pointer'}}
                  onClick={() => toggleBoolBag('agree')}
                  fontSize="xs"
                  mt={2}
                  ml={2}
                  fontFamily="Inter"
                >
                  By submitting this form, I agree to all program terms and conditions
                </StudentFormLabel>
              </Row>
            </UiFormControl>
          </>
        )}
      </UiCard>
    );
  };

  const handleOpenDeeplink = () => {
    window.open(TODAY_DEEPLINK, '_self');
  };

  return (
    <>
      <TextModal
        isShowing={isShowSuccessModal}
        title={`Thank you for submitting your application!`}
        logo="student-ease-logo"
        hideCloseButton
        onClickSubmit={isWebview ? handleOpenDeeplink : undefined}
        buttonTitle="Done"
      >
        <UiText mt={5} fontSize="lg">
          We’ll be in touch with you shortly!
        </UiText>
      </TextModal>
      <UploadImageModal
        isShowing={isShowUploadModal}
        onDismissModal={() => setIsShowUploadModal(false)}
        onSuccessUpload={onUploadSuccess}
        multiple
        limit={2}
        endpoint={STUDENT_ENDPOINTS.UPLOAD}
      />
      <UiApplicationsFormsLayout
        bg={{base: isClosed ? 'darkCream' : 'white', lg: 'white'}}
        height="100%"
        mb={5}
        mx={{base: 0, lg: spacing.ultra}}
        flexGrow={1}
      >
        <Col alignItems="center" textAlign="center" flexGrow={1}>
          {renderContent()}
        </Col>
        <Col alignItems="center" textAlign="center">
          <UiButton
            title="Submit"
            w={{base: '95%', lg: '25%'}}
            fontSize="xs"
            _hover={{
              background: 'baseColor',
              color: 'white',
            }}
            bg="fadedJade"
            alignItems="center"
            justifyContent="center"
            display="flex"
            onClick={onSubmit}
            borderRadius="10px"
            isDisabled={!canSubmit}
            mt={2}
            isLoading={loading}
          />

          {errorText && (
            <Row bg="red" p={2} w="100%" borderRadius={8} mt={4}>
              <Col mr={1}>
                <UiIcon as={HiExclamationCircle} boxSize="20px" color="white" />
              </Col>
              <Col>
                <UiText color="white" textAlign="left" fontWeight="400">
                  {errorText}
                </UiText>
              </Col>
            </Row>
          )}
        </Col>
      </UiApplicationsFormsLayout>
    </>
  );
};
export default memo(AmbassadorForm);
